import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import {
  fetchServerMembers, inviteServerMember, updateServerMember,
  removeServerMember, kickServerPlayer, fetchServerPlayers,
  playerServerAction
} from "src/actions/mine_craft/serversActions";
import { searchUsersCollection } from "src/actions/mine_craft/usersActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Input, message, Popconfirm, Select, Spin, Tag, Tooltip } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CrownOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  SyncOutlined,
  UserAddOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
import Card from "src/componets/Card";
import { shortFromNow } from "./_serverHelpers";
import Bans from "./_Bans";
import PlayerProfile from "./_PlayerProfile";

// crafty-servers has no concept of users or roles — it only knows an
// allowlist of gamertags plus a kick command. Roles live in the minecraft
// DB; inviting writes the roster row and, when there is a gamertag to
// write, the remote allowlist entry. Removing revokes both, and a kick is
// transient (the player keeps their allowlist entry and can rejoin).
const ROLES = [
  { value: 'member',    label: 'Member',    hint: 'Can join and play. No control over the server.' },
  { value: 'moderator', label: 'Moderator', hint: 'Start/stop/restart, install content, invite and kick.' },
  { value: 'admin',     label: 'Admin',     hint: 'Everything a moderator can do, plus managing roles.' }
];

const ROLE_COLORS: { [key: string]: string } = {
  member:    'default',
  moderator: 'blue',
  admin:     'gold'
};

// Mirrors User::GAMERTAG_REGEX in the minecraft app — lets the picker offer
// "invite this tag" for someone who has no account here yet.
const GAMERTAG_REGEX = /^[A-Za-z0-9 _.\-]{1,32}$/;
const SEARCH_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;
// Prefix marking a picker entry that is a raw gamertag rather than a user id.
const RAW = 'raw:';

const _Members = ({ server }: { server: ServerInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();
  const hosted = server?.hosted_server;

  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const [invitee, setInvitee] = useState<string | undefined>(undefined);
  const [inviteRole, setInviteRole] = useState('member');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState('');

  // Inline gamertag editor for a pending member.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingTag, setEditingTag] = useState('');

  // Which gamertag the profile drawer shows; null keeps it closed.
  const [profileTag, setProfileTag] = useState<string | null>(null);

  const searchTimer = useRef<any>(null);

  const serverId = server?.id;
  const ready = hosted?.state === 'ready';

  const loadMembers = useCallback(async () => {
    if (!serverId) return;

    setLoading(true);
    const resultAction = await dispatch(fetchServerMembers(serverId));
    setLoading(false);

    if (fetchServerMembers.fulfilled.match(resultAction)) {
      setMembers(resultAction.payload.members || []);
    }
  }, [serverId, dispatch]);

  // Who is connected right now, so a kick button can be offered against
  // real players rather than a free-text box.
  const loadOnline = useCallback(async () => {
    if (!serverId) return;

    const resultAction = await dispatch(fetchServerPlayers(serverId));

    if (fetchServerPlayers.fulfilled.match(resultAction)) {
      setOnline(resultAction.payload.players || []);
    } else {
      setOnline([]);
    }
  }, [serverId, dispatch]);

  // Debounced remote search. The endpoint matches the term against both
  // handles a player may be known by — username and gamertag.
  const searchUsers = useCallback((value: string) => {
    setTerm(value);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    searchTimer.current = setTimeout(async () => {
      if (!server) return;

      setSearching(true);
      try {
        const { users, ids } = await searchUsersCollection({
          app_id: server.app_id,
          term:   value || undefined,
          limit:  SEARCH_LIMIT
        });
        setUserOptions(ids.map((id: number) => users[id]).filter(Boolean));
      } catch (error) {
        console.error(error);
        setUserOptions([]);
      }
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);
  }, [server]);

  useEffect(() => {
    if (!hosted) return;

    loadMembers().catch(console.error);
    if (ready) loadOnline().catch(console.error);
  }, [hosted, ready, loadMembers, loadOnline]);

  if (!server || !hosted) {
    return (
      <Card>
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Not hosted through crafty-servers</div>
          <div className='o-hint'>Roles and invites only apply to servers we host.</div>
        </div>
      </Card>
    );
  }

  function fail(payload: any, fallback: string) {
    message.error(Array.isArray(payload) ? payload[0] : fallback);
  }

  async function invite() {
    if (!serverId || !invitee) return;

    const data: any = { role: inviteRole };
    if (String(invitee).startsWith(RAW)) {
      data.gamertag = String(invitee).slice(RAW.length);
    } else {
      data.user_id = invitee;
    }

    setBusy(true);
    const resultAction = await dispatch(inviteServerMember({ id: serverId, data }));
    setBusy(false);

    if (inviteServerMember.fulfilled.match(resultAction)) {
      const member = resultAction.payload?.member;
      message.success(member?.pending
        ? `${member.user_name || 'Player'} added — set a gamertag to grant access`
        : `${member?.gamertag || 'Player'} invited`);
      setInvitee(undefined);
      setTerm('');
      loadMembers().catch(console.error);
    } else {
      fail(resultAction.payload, 'Invite failed');
    }
  }

  async function saveMember(memberId: number, data: { role?: string, gamertag?: string }, successText: string) {
    if (!serverId) return false;

    const resultAction = await dispatch(updateServerMember({ id: serverId, memberId, data }));

    if (updateServerMember.fulfilled.match(resultAction)) {
      message.success(successText);
      loadMembers().catch(console.error);
      return true;
    }

    fail(resultAction.payload, 'Update failed');
    return false;
  }

  async function saveGamertag(memberId: number) {
    const tag = editingTag.trim();
    if (!tag) return;

    setBusy(true);
    const ok = await saveMember(memberId, { gamertag: tag }, `${tag} allowlisted`);
    setBusy(false);

    if (ok) {
      setEditingId(null);
      setEditingTag('');
    }
  }

  async function remove(memberId: number) {
    if (!serverId) return;

    const resultAction = await dispatch(removeServerMember({ id: serverId, memberId }));

    if (removeServerMember.fulfilled.match(resultAction)) {
      message.success('Member removed');
      loadMembers().catch(console.error);
    } else {
      fail(resultAction.payload, 'Remove failed');
    }
  }

  async function kick(tag: string) {
    if (!serverId) return;

    const resultAction = await dispatch(kickServerPlayer({ id: serverId, gamertag: tag }));

    if (kickServerPlayer.fulfilled.match(resultAction)) {
      message.success(`${tag} kicked`);
      loadOnline().catch(console.error);
    } else {
      fail(resultAction.payload, 'Kick failed');
    }
  }

  // op / deop / heal run as console commands against a connected player.
  async function playerAction(tag: string, action: 'op' | 'deop' | 'heal') {
    if (!serverId) return;

    const resultAction = await dispatch(playerServerAction({ id: serverId, gamertag: tag, action }));

    if (playerServerAction.fulfilled.match(resultAction)) {
      message.success(`${action} sent to ${tag}`);
    } else {
      fail(resultAction.payload, `${action} failed`);
    }
  }

  // The config mirror is crafty's own payload; only an explicit false
  // disables heal — an unknown value leaves the button usable and lets
  // the backend's 409 answer authoritatively.
  const cheats = hosted?.config?.cheats;

  const onlineTags = new Set(online.map((player: any) => String(player.name).toLowerCase()));
  const isOwner = (member: any) => member.user_id && member.user_id === server.user_id;
  const memberUserIds = new Set(members.map(m => m.user_id).filter(Boolean));
  const memberTags = new Set(members.map(m => String(m.gamertag || '').toLowerCase()).filter(Boolean));

  const selectedUser = typeof invitee === 'number'
    ? userOptions.find(option => option.id === invitee)
    : undefined;

  // A user already on the roster is offered but disabled, so the reason a
  // name doesn't work is visible instead of it silently vanishing.
  const inviteOptions: any[] = userOptions.map(user => {
    const alreadyIn = memberUserIds.has(user.id) ||
      (user.gamertag && memberTags.has(String(user.gamertag).toLowerCase()));

    return {
      value: user.id,
      disabled: !!alreadyIn,
      label: user.name || user.gamertag || `#${user.id}`,
      user,
      alreadyIn
    };
  });

  // Someone who has never registered here can still be allowlisted by tag.
  const trimmedTerm = term.trim();
  const exactMatch = userOptions.some(user =>
    String(user.gamertag || '').toLowerCase() === trimmedTerm.toLowerCase() ||
    String(user.name || '').toLowerCase() === trimmedTerm.toLowerCase()
  );

  if (trimmedTerm && !exactMatch && GAMERTAG_REGEX.test(trimmedTerm) && !memberTags.has(trimmedTerm.toLowerCase())) {
    inviteOptions.push({
      value: `${RAW}${trimmedTerm}`,
      label: trimmedTerm,
      raw:   trimmedTerm
    });
  }

  const MemberRow = ({ member }: { member: any }) => {
    const owner   = isOwner(member);
    const pending = member.pending || !member.gamertag;
    const isLive  = !pending && onlineTags.has(String(member.gamertag).toLowerCase());
    const editing = editingId === member.id;

    return (
      <div className={`v-srv-member ${pending ? 'o-pending' : ''}`}>
        <div className='o-who'>
          {member.user_avatar_url
            ? <img className='o-avatar' src={member.user_avatar_url} alt='' />
            : <span className='o-avatar o-empty' />}

          <div className='o-names'>
            <div
              className={`o-tag ${member.gamertag ? 'o-clickable' : ''}`}
              title={member.gamertag ? 'View player profile' : undefined}
              onClick={() => member.gamertag && setProfileTag(member.gamertag)}
            >
              {member.gamertag || member.user_name || 'Unnamed'}
              {isLive && <span className='o-online' title='Online now' />}
            </div>
            <div className='o-sub'>
              {/* Whichever handle isn't already the headline. */}
              {member.gamertag && member.user_name && <span>{member.user_name}</span>}
              {!member.user_name &&
                <span className='o-unlinked' title='No OneSky account carries this gamertag yet'>
                  no linked account
                </span>}
              {member.created_at && <span>invited {shortFromNow(member.created_at)}</span>}
            </div>
          </div>
        </div>

        {pending &&
          (editing
            ? <div className='o-edit'>
                <Input
                  autoFocus
                  size='small'
                  placeholder='Gamertag'
                  maxLength={32}
                  value={editingTag}
                  disabled={busy}
                  onChange={e => setEditingTag(e.target.value)}
                  onPressEnter={() => saveGamertag(member.id)}
                />
                <Button
                  size='small'
                  type='primary'
                  icon={<CheckOutlined />}
                  disabled={busy || !editingTag.trim()}
                  onClick={() => saveGamertag(member.id)}
                  title='Save gamertag'
                />
                <Button
                  size='small'
                  icon={<CloseOutlined />}
                  disabled={busy}
                  onClick={() => { setEditingId(null); setEditingTag(''); }}
                  title='Cancel'
                />
              </div>
            : <Tooltip title='Has a role, but no gamertag to allowlist — they cannot connect yet'>
                <Tag color='warning' icon={<ExclamationCircleOutlined />} className='o-pending-tag'>
                  no gamertag
                </Tag>
              </Tooltip>
          )
        }

        {/* Role and actions travel together so that in a half-width column
            they drop to a second line as one right-aligned block, instead of
            each control wrapping on its own. */}
        <div className='o-controls'>
          {/* Ahead of the role select: filling this in is what actually
              grants access, so it reads before the role it applies to. */}
          {write_minecraft && !owner && pending && !editing &&
            <Button
              size='small'
              icon={<EditOutlined />}
              onClick={() => { setEditingId(member.id); setEditingTag(''); }}
            >
              Set gamertag
            </Button>
          }

          <div className='o-role'>
            {owner
              ? <Tooltip title='The server owner is always an admin'>
                  <Tag color='gold' icon={<CrownOutlined />}>owner</Tag>
                </Tooltip>
              : write_minecraft
                ? <Select
                    size='small'
                    value={member.role}
                    onChange={value => saveMember(member.id, { role: value }, 'Role updated')}
                    options={ROLES.map(role => ({ value: role.value, label: role.label }))}
                  />
                : <Tag color={ROLE_COLORS[member.role]}>{member.role}</Tag>
            }
          </div>

          {/* No Kick here — kicking is a live-session action and belongs
              with the connected players in Online now. Removing a member
              revokes access outright, which is a roster decision. */}
          {write_minecraft && !owner &&
            <div className='o-actions'>
              <Popconfirm
                title={`Remove ${member.gamertag || member.user_name}? This revokes their access.`}
                onConfirm={() => remove(member.id)}
              >
                <Button size='small' danger>Remove</Button>
              </Popconfirm>
            </div>
          }
        </div>
      </div>
    );
  };

  return (
    <>
      {write_minecraft &&
        <Card className='v-srv-invite'>
          <h4 className='v-content-title'>Invite a player</h4>

          <div className='o-row'>
            <Select
              className='o-search'
              showSearch
              allowClear
              value={invitee}
              placeholder='Search by username or gamertag'
              filterOption={false}
              disabled={busy || !ready}
              loading={searching}
              onSearch={searchUsers}
              onFocus={() => searchUsers('')}
              onChange={value => setInvitee(value)}
              onClear={() => { setInvitee(undefined); setTerm(''); }}
              notFoundContent={searching ? <Spin size='small' /> : 'No matching players'}
              optionLabelProp='label'
            >
              {inviteOptions.map(option => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                >
                  {option.raw
                    ? <div className='v-srv-option'>
                        <span className='o-avatar o-empty' />
                        <div className='o-lines'>
                          <div className='o-name'>{option.raw}</div>
                          <div className='o-meta'>Invite this gamertag — no account here yet</div>
                        </div>
                      </div>
                    : <div className='v-srv-option'>
                        {option.user.avatar_url
                          ? <img className='o-avatar' src={option.user.avatar_url} alt='' />
                          : <span className='o-avatar o-empty' />}
                        <div className='o-lines'>
                          <div className='o-name'>{option.user.name || `#${option.user.id}`}</div>
                          <div className='o-meta'>
                            {option.user.gamertag
                              ? <span className='o-gt'>{option.user.gamertag}</span>
                              : <span className='o-none'>no gamertag</span>}
                            {option.alreadyIn && <span className='o-in'>already on the roster</span>}
                          </div>
                        </div>
                      </div>
                  }
                </Select.Option>
              ))}
            </Select>

            <Select
              className='o-role-select'
              value={inviteRole}
              onChange={setInviteRole}
              disabled={busy || !ready}
              optionLabelProp='label'
            >
              {ROLES.map(role => (
                <Select.Option key={role.value} value={role.value} label={role.label}>
                  <div className='v-srv-option o-compact'>
                    <div className='o-lines'>
                      <div className='o-name'>{role.label}</div>
                      <div className='o-meta'>{role.hint}</div>
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>

            <Button
              type='primary'
              icon={<UserAddOutlined />}
              disabled={busy || !ready || !invitee}
              onClick={invite}
            >
              Invite
            </Button>
          </div>

          {/* The consequence of picking an account with no gamertag is
              spelled out before the click, not after. */}
          {selectedUser && !selectedUser.gamertag
            ? <div className='o-notice'>
                <ExclamationCircleOutlined />
                <span>
                  <b>{selectedUser.name || 'This player'}</b> has no gamertag yet. They will get the role
                  now, but stay off the server's allowlist until a gamertag is set — you can add one
                  from the list below at any time.
                </span>
              </div>
            : <div className='o-hint'>
                Adds the gamertag to this server's allowlist immediately. The allowlist only
                takes effect while the server runs with access set to <b>allowlist</b>.
              </div>
          }
        </Card>
      }

      {/* Roster left, live connections right — the two answer different
          questions ("who may join" vs "who is on"), so they read better
          side by side than stacked. */}
      <div className='v-srv-roster'>
      <Card className='v-srv-members'>
        <div className='o-head'>
          <h4 className='v-content-title'>Members</h4>
          <span className='o-count'>{members.length}</span>
          <Button
            className='o-icon-btn'
            icon={<SyncOutlined />}
            title='Refresh'
            aria-label='Refresh members'
            loading={loading}
            onClick={() => loadMembers()}
          />
        </div>

        {loading && !members.length &&
          <div className='o-list'>
            {[0, 1, 2].map(index => <div key={index} className='v-srv-member o-skeleton' />)}
          </div>
        }

        <div className='o-list'>
          {members.map(member => <MemberRow key={member.id} member={member} />)}
        </div>

        {!loading && !members.length &&
          <div className='v-srv-empty'>
            <InboxOutlined className='o-icon' />
            <div className='o-title'>No members yet</div>
            <div className='o-hint'>
              The owner always has full access{write_minecraft ? ' — invite players above' : ''}.
            </div>
          </div>
        }
      </Card>

      {/* Always rendered, so the two columns stay put instead of the layout
          reflowing every time the last player disconnects. */}
      <Card className='v-srv-online'>
          <div className='o-head'>
            <h4 className='v-content-title'>Online now</h4>
            <span className='o-count'>{online.length}</span>
          </div>

          <div className='o-list'>
            {online.map((player: any, index: number) => {
              const known = memberTags.has(String(player.name).toLowerCase());

              return (
                <div key={index} className='v-srv-member'>
                  <div className='o-who'>
                    <span className='o-avatar o-empty' />
                    <div className='o-names'>
                      <div
                        className='o-tag o-clickable'
                        title='View player profile'
                        onClick={() => setProfileTag(player.name)}
                      >
                        {player.name}
                        <span className='o-online' title='Online now' />
                      </div>
                      <div className='o-sub'>
                        {player.joinedAt &&
                          <span title={formattedDate(player.joinedAt).dateTime}>
                            joined {shortFromNow(player.joinedAt)}
                          </span>}
                      </div>
                    </div>
                  </div>

                  {/* A player can be connected without a roster row when the
                      server isn't running in allowlist mode. */}
                  {!known && <Tag color='warning'>not on the roster</Tag>}

                  {write_minecraft &&
                    <div className='o-actions'>
                      {/* op/deop are transient console grants, same weight as
                          kick — no confirm, a toast reports the outcome. */}
                      <Button size='small' onClick={() => playerAction(player.name, 'op')}>Op</Button>
                      <Button size='small' onClick={() => playerAction(player.name, 'deop')}>Deop</Button>
                      <Tooltip title={cheats === false ? 'Heal needs cheats enabled on this server' : 'Run instant_health on this player'}>
                        <Button
                          size='small'
                          disabled={cheats === false}
                          onClick={() => playerAction(player.name, 'heal')}
                        >
                          Heal
                        </Button>
                      </Tooltip>
                      <Popconfirm
                        title={`Kick ${player.name}? They keep access and can rejoin.`}
                        onConfirm={() => kick(player.name)}
                      >
                        <Button size='small' icon={<UserDeleteOutlined />}>Kick</Button>
                      </Popconfirm>
                    </div>
                  }
                </div>
              );
            })}
          </div>

          {!online.length &&
            <div className='v-srv-empty'>
              <InboxOutlined className='o-icon' />
              <div className='o-title'>Nobody connected</div>
              <div className='o-hint'>
                {ready
                  ? 'Players show up here as soon as they join.'
                  : 'The server has to be running before players can connect.'}
              </div>
            </div>
          }
        </Card>
      </div>

      <Bans server={server} onSelectPlayer={setProfileTag} />

      <PlayerProfile server={server} gamertag={profileTag} onClose={() => setProfileTag(null)} />
    </>
  );
}

export default _Members;
